import { OutdatedBadge } from "@src/components/outdated_badge";
import { DUALIS_TECHNOLOGY, REQUEST_STATUS, ROLES } from "@src/constants";
import {
  ProjectInformationRenderer,
  RequestedRoleRenderer,
  TextSubtextRenderer,
  StatusRenderer,
} from "@src/custom_renderer";
import { TeamLeadRequestOverviewItem } from "@src/types";
import { FrameworkComponents } from "@src/types/aggrid_types";
import {
  AdapterCellRendererProps,
  ProjectInformationRendererValue,
  RequestedRoleRendererValue,
  StatusRendererValue,
  TextSubtextRendererValue,
} from "@src/types/custom_renderer_types";
import {
  cellWrapText,
  columnTypes,
  paginationGridOptions,
} from "@src/utils/aggrid_utils";
import {
  CellStyle,
  ColDef,
  GetQuickFilterTextParams,
  GridOptions,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-community";
import { CustomCellRendererProps } from "ag-grid-react";
import dayjs from "dayjs";

import { getBadgeVariant } from "./request_overview_utils";
import { ActionRequiredRenderer, TagRenderer } from "../custom_renderer";

const centerCellText: CellStyle = { justifyContent: "center" };

function getFormattedDate(date: Date): string {
  return dayjs(date).format("DD/MM/YYYY");
}

function frameworkComponents(): FrameworkComponents {
  return {
    renderRequestedRole: (props: AdapterCellRendererProps) => {
      return <RequestedRoleRenderer props={props} />;
    },
    renderTextSubtext: (props: CustomCellRendererProps) => {
      return <TextSubtextRenderer props={props} />;
    },
    renderProjectPhase: (props: CustomCellRendererProps) => {
      return <ProjectInformationRenderer props={props} />;
    },
    renderTag: (props: CustomCellRendererProps) => {
      return <TagRenderer variant={props.value} />;
    },
    renderOutdatedBadge: (props: CustomCellRendererProps) => {
      return <OutdatedBadge daysUntilDeletion={props.data.daysUntilDeletion} />;
    },
    renderRequiredAction: (props: CustomCellRendererProps) => {
      return <ActionRequiredRenderer actionRequired={props.value} />;
    },
    renderStatus: (props: AdapterCellRendererProps) => {
      return <StatusRenderer props={props} />;
    },
  };
}

const projectNameColDef: ColDef = {
  headerName: "Project Name",
  field: "projectName",
  cellStyle: cellWrapText,
  minWidth: 100,
  autoHeight: true,
  filter: "agSetColumnFilter",
};

const requestIdColDef: ColDef = {
  headerName: "Request ID",
  field: "projectRoleRequestId",
  cellStyle: cellWrapText,
  wrapHeaderText: true,
  minWidth: 100,
  maxWidth: 250,
  autoHeight: true,
  wrapText: true,
};

const tagColDef: ColDef = {
  headerName: "Tag",
  field: "tag",
  cellRenderer: "renderTag",
  width: 130,
  suppressSizeToFit: true,
  valueGetter: (params: ValueGetterParams) => {
    return getBadgeVariant(params);
  },
  cellRendererSelector: (props: CustomCellRendererProps) =>
    props.value === "Outdated"
      ? { component: "renderOutdatedBadge" }
      : { component: "renderTag" },
};

const requestedRoleColDef: ColDef = {
  headerName: "Requested Role",
  field: "requestedRoleName",
  cellRenderer: "renderRequestedRole",
  cellStyle: cellWrapText,
  minWidth: 370,
  maxWidth: 500,
  autoHeight: true,
  lockPosition: true,
  lockVisible: true,
  filter: "agSetColumnFilter",
  getQuickFilterText: (
    params: GetQuickFilterTextParams<TeamLeadRequestOverviewItem>
  ) => {
    return `${params.data.projectRoleRequestId} ${params.data.requestedRoleName}`;
  },
  cellRendererParams: {
    adapter: (
      rowData: TeamLeadRequestOverviewItem
    ): RequestedRoleRendererValue => ({
      roleRequestId: rowData.projectRoleRequestId,
      standardRoleName: rowData.requestedRoleName,
      basicRoleName: rowData.basicRoleName,
      roleSpecificationName: rowData.roleSpecificationName,
      actionRequired: rowData.actionRequired,
      isUpdated: rowData.isUpdated,
      isCommentAvailable: rowData.isCommentAvailable && !rowData.isCommentRead,
      isTeamLeadAssigned: true,
      isDraft: rowData.status === REQUEST_STATUS.DRAFT,
      latestComment: rowData.latestComment,
    }),
  },
  cellClass: "full-width-cell",
};

const projectPhaseColDef: ColDef = {
  headerName: "Project Phase",
  field: "projectPhase",
  cellStyle: cellWrapText,
  minWidth: 100,
  maxWidth: 350,
  autoHeight: true,
  filter: "agSetColumnFilter",
};

const technologiesColDef: ColDef = {
  headerName: "Technology",
  field: "technologies",
  cellStyle: cellWrapText,
  minWidth: 100,
  maxWidth: 250,
  filter: "agSetColumnFilter",
};

const projectAndPhaseColDef: ColDef = {
  headerName: "Project Information",
  headerTooltip: "Project / Phase / Technology / Project Lead",
  cellRenderer: "renderProjectPhase",
  field: "projectNameAndPhase",
  cellStyle: cellWrapText,
  minWidth: 400,
  maxWidth: 500,
  autoHeight: true,
  filter: "agSetColumnFilter",
  getQuickFilterText: (
    params: GetQuickFilterTextParams<TeamLeadRequestOverviewItem>
  ) => {
    return `${params.data.projectName} ${params.data.projectPhase}`;
  },
  valueGetter: (params: ValueGetterParams): ProjectInformationRendererValue => {
    return {
      projectName: params.data.projectName,
      projectPhase: params.data.projectPhase,
      technology: params.data.technologies
        ?.filter(
          (technology: string) => technology !== DUALIS_TECHNOLOGY.HYDROGEN
        )
        .join(", "),
      projectLeadEmployeeName: params.data.projectLeadEmployeeName,
      isHydrogen: params.data.technologies?.includes(
        DUALIS_TECHNOLOGY.HYDROGEN
      ),
    };
  },
  comparator(
    valueA: ProjectInformationRendererValue,
    valueB: ProjectInformationRendererValue
  ) {
    return valueA.projectName > valueB.projectName ? 1 : -1;
  },
  cellClass: "full-width-cell",
};

const regionCountryColDef: ColDef = {
  headerName: "Region/Country",
  field: "regionAndCountry",
  cellRenderer: "renderTextSubtext",
  cellStyle: cellWrapText,
  minWidth: 232,
  maxWidth: 350,
  autoHeight: true,
  filter: "agSetColumnFilter",
  getQuickFilterText: (
    params: GetQuickFilterTextParams<TeamLeadRequestOverviewItem>
  ) => {
    return `${params.data.region} ${params.data.country}`;
  },
  valueGetter: (params: ValueGetterParams): TextSubtextRendererValue => {
    return {
      text: params.data.region,
      subtext: params.data.country,
    };
  },
  comparator(
    valueA: TextSubtextRendererValue,
    valueB: TextSubtextRendererValue
  ) {
    return valueA.text > valueB.text ? 1 : -1;
  },
};

const countryColDef: ColDef = {
  headerName: "Country",
  field: "country",
  cellStyle: cellWrapText,
  minWidth: 100,
  maxWidth: 350,
  autoHeight: true,
  filter: "agSetColumnFilter",
};

const regionColDef: ColDef = {
  headerName: "Region",
  field: "region",
  cellStyle: cellWrapText,
  minWidth: 100,
  maxWidth: 350,
  autoHeight: true,
  filter: "agSetColumnFilter",
};

const workloadStartColDef: ColDef = {
  headerName: "WL Start",
  field: "roleRequestStart",
  headerTooltip: "Workload Start Date",
  valueFormatter: (params: ValueFormatterParams) => {
    if (params.data?.roleRequestStart) {
      return getFormattedDate(params.data.roleRequestStart);
    }
    return "N/A";
  },
  filterValueGetter: (params: ValueGetterParams) => {
    return getFormattedDate(params.data.roleRequestStart);
  },
  minWidth: 150,
  maxWidth: 200,
  filter: "agSetColumnFilter",
  resizable: false,
};

const requestSentColDef: ColDef = {
  headerName: "Request Sent",
  field: "requestSentDate",
  valueFormatter: (params: ValueFormatterParams) => {
    if (params.data?.requestSentDate) {
      return getFormattedDate(params.data.requestSentDate);
    }
    return "N/A";
  },
  filterValueGetter: (params: ValueGetterParams) => {
    return getFormattedDate(params.data.requestSentDate);
  },
  minWidth: 150,
  maxWidth: 200,
  filter: "agSetColumnFilter",
  resizable: false,
};

const statusColDef: ColDef = {
  headerName: "Status",
  field: "status",
  cellRenderer: "renderStatus",
  cellRendererParams: {
    adapter: (rowData: TeamLeadRequestOverviewItem): StatusRendererValue => ({
      status: rowData.status,
      assignedEmployeeFullName: rowData.assignedTeamMember,
    }),
  },
  minWidth: 150,
  maxWidth: 170,
  filter: "agSetColumnFilter",
  resizable: false,
};

const requiredActionColDef: ColDef = {
  headerName: "Required Action",
  field: "actionRequired",
  cellRenderer: "renderRequiredAction",
  cellStyle: centerCellText,
  minWidth: 100,
  maxWidth: 180,
  filter: "agSetColumnFilter",
};

function getColumns(): ColDef[] {
  return [
    requestedRoleColDef,
    requestIdColDef,
    projectNameColDef,
    tagColDef,
    projectPhaseColDef,
    technologiesColDef,
    projectAndPhaseColDef,
    regionCountryColDef,
    countryColDef,
    regionColDef,
    workloadStartColDef,
    requestSentColDef,
    statusColDef,
    requiredActionColDef,
  ];
}

export function createGridOptions(): GridOptions {
  return {
    ...paginationGridOptions,
    paginationPageSize: 20,
    components: frameworkComponents(),
    rowHeight: 50,
    domLayout: "autoHeight",
    suppressContextMenu: true,
    alwaysMultiSort: true,
    columnDefs: getColumns(),
    defaultColDef: {
      ...columnTypes.default,
    },
    context: {
      view: ROLES.TEAM_LEAD,
    },
  };
}
